import Game from "Game";
import { IGameState } from "State/Game/IGameState";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import { applyDefaultButtonAnimation } from "System/UI/UIHelper";
import { FBTrackingEvent } from "Utility/Tracking/FBTrackingEvent";
import { PlayerActions } from "State/Player/PlayerActions";
import { UIActions } from "State/UI/UIActions";
import Renderable from "Component/Renderable";
import Interactable from "Component/Interactable";
import { FTUXType } from "Player/PlayerProperty/Progress/FTUXType";
import AnimationComponent from "Component/Animation";
import { PlayerTask } from "State/Player/PlayerTasks";
import HarvestTimer from "System/Timer/Timers/HarvestTimer";
import DateHelper from "Utility/DateHelper";
import { resetEventHandlerData, track } from "Utility/Tracking/Track";
import { FTUXTrackingEventName } from "Utility/Tracking/TrackingEvent";
import { DRAGONBONES_PATH } from "Constants/Dragonbones";
import { addDragonbonesResources } from "Utility/Resource/ResourceHelper";
import Armature from "Prototype/Armature";
import { StartAnimationClip } from "System/Animation/AnimationHelper";
import { Alpha, DragonbonesFadeIn, ScalePulseLoop } from "System/Animation/Clip";
import Point from "Utility/Point";
import ParticleEmitter from "Prototype/Effect/ParticleEmitter";
import { ThemeSettings } from "Constants/Theme";
import { FieldStateHelper } from "State/Player/Fields/FieldStateHelper";
import { PopupID } from "State/UI/PopupID";
import { Random } from "Utility/Random";
import { FTUXStateHelper } from "State/Game/FTUX/FTUXStateHelper";
import { HarvestButtonLayout } from "Layouts/Harvest/HarvestButtonLayout";
import SpriteComponent from "Component/Render/Sprite";
import TextComponent from "Component/Render/Text";
import TransformComponent from "Component/Transform";
import Scene from "Scene/Scene";
import RelativePosition from "Component/Render/RelativePosition";
import RenderSystem, { RenderSystemEvent } from "System/Render/RenderSystem";
import PIXIRenderer from "System/Render/PIXIRenderer";
import CleanupManager from "Utility/CleanupManager";

const idleDBName: string = 'harvest_button';
const idleDBArmatureName: string = 'Armature';
const idleDBAnimationName: string = 'animtion0';

export default class HarvestButton extends GameObject<PIXI.Container>  {
    private harvestEmitters: ParticleEmitter[] = null;
    private idleAnimationTimeout!: () => void;
    private timer: HarvestTimer;
    private active!: boolean;
    private readonly layout: any;
    private idleArmature: Armature | null = null;
    private readonly cleanupManager: CleanupManager = new CleanupManager();

    constructor() {
        super();
        this.layout = new HarvestButtonLayout();
    }

    public onCreate(props: { scene: Scene }): void {
        this.addChild(this.root);
        this.layout.harvestButton.getComponent(SpriteComponent).texture = ThemeSettings.HARVEST_BUTTON_HUD_CACHE_KEY;
        this.layout.harvestButton.getComponent(TransformComponent).scale.set(ThemeSettings.HARVEST_BUTTON_SCALE);
        this.layout.harvestButton.getComponent(TransformComponent).y = ThemeSettings.HARVEST_BUTTON_Y;
        this.layout.harvestText.getComponent(TextComponent).style = Game.style.getStyle(ThemeSettings.HARVEST_BUTTON_TEXT_STYLE_KEY);
        this.layout.decor.getComponent(Renderable).visible = ThemeSettings.eventActive;
        this.layout.root.addComponent(RelativePosition, { x: 0.79, y: 0.92 });

        const offset = 3;
        const coinTransform = this.layout.coin.getComponent(TransformComponent);
        const textTransform = this.layout.harvestText.getComponent(TransformComponent);

        coinTransform.x = textTransform.width / 2 + offset;
        textTransform.x = -coinTransform.width / 2 - offset;

        applyDefaultButtonAnimation(this.layout.harvestButton);
        this.layout.harvestButton.addComponent(Interactable, { toggleable: true });
        this.layout.harvestButton.getComponent(Interactable).tintOnDisable = true;
        this.layout.harvestButton.on(GameObjectEvent.SELECTED, this.handleHarvestClick);
        this.layout.root.on(GameObjectEvent.DESTROYED, this.handleDestroy);
        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);

        const state: IGameState = Game.store.getState();
        const rootRenderable = this.layout.root.getComponent(Renderable);

        if (!this.showOnCreate(state)) {
            rootRenderable.visible = false;
            rootRenderable.alpha = 0;

            if (this.showFTUX(state)) {
                PlayerActions.addTasks([{ task: PlayerTask.FIELD_FTUX }]);
            }
        }

        props.scene.on('EnableHarvestButton', this.enableHarvestButton);
        this.cleanupManager.add(() => {
            props.scene.off('EnableHarvestButton', this.enableHarvestButton);
        })

        this.timer = new HarvestTimer();
        this.timer.onUpdate = this.updateText;
        this.timer.onComplete = this.handleHarvest;
        this.loadDBAssets().then(this.onDBAssetsLoaded);
        this.handleResize();
    }

    public get root(): GameObject {
        return this.layout.root;
    }

    public get ftuxArrow(): GameObject {
        return this.layout.ftuxArrow;
    }

    public setInteractive(value: boolean): void {
        this.layout.harvestButton.getComponent(Interactable).enabled = value;

        if (value) {
            this.layout.harvestButton.getComponent(Interactable).toggle(true);
        }
    }

    private readonly handleHarvestClick = (): void => {
        const state: IGameState = Game.store.getState();

        if (!FieldStateHelper.canHarvest(state)) return;

        const isFtuxCompleted = FTUXStateHelper.isFtuxCompleted(
            state.playerState,
            FTUXType.HARVEST
        );

        if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_14_HarvestButtonClicked)) {
            Game.sdk.gameTracking(FBTrackingEvent.FTUE_14_HarvestButtonClicked);
            PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_14_HarvestButtonClicked);

            track('stepId', 14);
            track('stepName', FBTrackingEvent.FTUE_14_HarvestButtonClicked);
            resetEventHandlerData(FTUXTrackingEventName.ftux, Game.store.getState());
        }

        UIActions.showPopup(PopupID.HARVEST_MENU, { playAutoFlow: !isFtuxCompleted});

        if (!isFtuxCompleted) {
            Game.scene.getActiveScene().emit('HideHarvestFUTX');
        }
    };

    private readonly enableHarvestButton = () => {
        if (this.layout.root.destroyed) return;
        this.layout.harvestButton.getComponent(Interactable).enabled = true;
    }

    public readonly updateText = (state: IGameState = Game.store.getState()): void => {
        if (this.layout.root.destroyed) return;

        const coinRenderable = this.layout.coin.getComponent(Renderable);
        const textComponent = this.layout.harvestText.getComponent(TextComponent);
        const time: Date = this.timer.getDate(state);
        const { duration, format } = this.timer.getDuration(state);

        const text: string = time === null
            ? Game.locale.getText('Harvest')
            : DateHelper.timeTo(DateHelper.now, DateHelper.getDate(time.getTime() + duration), format);

        textComponent.text = text;
        coinRenderable.visible = time === null;

        this.layout.harvestText.getComponent(TransformComponent).x = time === null ? -20 : 0;
    };

    public readonly handleHarvest = (state: IGameState = Game.store.getState()): void => {
        const coinRenderable = (this.layout.coin as GameObject).getComponent<Renderable>(Renderable);
        const buttonInteractable: Interactable = this.layout.harvestButton.getComponent(Interactable);

        // all these should exist anyway
        if (!this.timer || !buttonInteractable || !coinRenderable) return;

        const isTimerComplete: boolean = this.timer.isCompleted(state);

        // If the status has changed since last checked
        if (this.active !== isTimerComplete) {
            buttonInteractable.enabled = isTimerComplete;
            coinRenderable.visible = isTimerComplete;

            if (isTimerComplete) {
                if (this.harvestEmitters === null && ThemeSettings.HARVEST_BUTTON_PARTICLE_TEXTURE_KEYS.length > 0) {
                    this.addHarvestParticleEffects(ThemeSettings.HARVEST_BUTTON_PARTICLE_TEXTURE_KEYS);

                    this.harvestEmitters.forEach((emitter: ParticleEmitter) =>
                        setTimeout(emitter.start, Random.int(0, 750)),
                    );
                }

                this.playIdleAnimation();
            } else {
                this.removeParticles();

                // sanity check
                if (this.idleArmature?.scene) {
                    StartAnimationClip(
                        new Alpha(this.idleArmature, {
                            startAlpha: 1,
                            endAlpha: 0,
                            duration: 150,
                        }),
                    );
                }
            }

            this.active = isTimerComplete;
        }

        this.updateText();
    };

    protected showOnCreate(state: IGameState = Game.store.getState()): boolean {
        return (
            state.playerState.fields.length > 0 &&
            FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.HARVEST)
        );
    }

    protected showFTUX(state: IGameState = Game.store.getState()): boolean {
        return (
            state.playerState.fields.length > 0 &&
            !FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.HARVEST)
        );
    }

    private async loadDBAssets(): Promise<void> {
        addDragonbonesResources(idleDBName, `${DRAGONBONES_PATH}/HarvestButton/`);
        await Game.resource.load();
    }

    private readonly onDBAssetsLoaded = () => {
        if (this.layout.root.destroyed) return;

        const state = Game.store.getState();
        const button = this.layout.harvestButton;

        this.idleArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: idleDBName,
            armatureName: idleDBArmatureName,
            animationName: idleDBAnimationName,
            alpha: 0,
            playOnStart: false,
            playTimes: 1,
            position: new Point(button.container.width * 0.5, button.container.height * 0.5),
            pivot: new Point(115, 60),
        });
        button.addChild(this.idleArmature);

        if (this.timer.isCompleted(state) && this.showOnCreate(state)) {
            this.playIdleAnimation();
        }
    };

    private readonly playIdleAnimation = () => {
        if (this.layout.root.destroyed || !this.idleArmature || !this.active) return;

        if (!this.idleArmature.getComponent(AnimationComponent)?.isPlaying) {
            StartAnimationClip(new DragonbonesFadeIn(this.idleArmature));
        }

        StartAnimationClip(
            new ScalePulseLoop(this.layout.root, {
                scale: new Point(1.05, 1.05),
                yoyo: true,
                repeat: 1,
                duration: 700,
            }),
        );

        if (this.idleAnimationTimeout) {
            Game.ticker.clearTimeout(this.idleAnimationTimeout);
        }

        this.idleAnimationTimeout = Game.ticker.setTimeout(this.playIdleAnimation, 4600);
    };

    private readonly handleDestroy = () => {
        if (this.idleAnimationTimeout) {
            Game.ticker.clearTimeout(this.idleAnimationTimeout);
        }
    };

    private readonly handleResize = () => {
        this.layout.root.getComponent(TransformComponent).y = PIXIRenderer.getOffsetY() * 0.9;
    };

    private addHarvestParticleEffects(textures: string[]): void {
        const data = Game.resource.get(
            ThemeSettings.HARVEST_BUTTON_PARTICLE_RESOURCE_DATA?.key ?? 'harvest_particle_data',
        );

        this.removeParticles();
        this.harvestEmitters = [];
        const nodeCount: number = 3;

        // Add emitters in a line along the button to emit particles all along the button
        for (let i = 0; i < nodeCount; i++) {
            const harvestParticleEmitter: ParticleEmitter = GameObject.Instantiate(ParticleEmitter, {
                textures,
                data,
                scale: new Point(0.5, 0.5),
                x: -110 + i * 75,
                y: i === 0 || i === nodeCount - 1 ? -40 : 0,
                elapsedTime: Date.now() + 500 * Math.random(),
            });
            this.harvestEmitters.push(harvestParticleEmitter);
            this.layout.particlesContainer.addChild(harvestParticleEmitter);
        }
    }

    private removeParticles(): void {
        if (this.harvestEmitters !== null) {
            this.harvestEmitters.forEach(emitter => {
                emitter.destroy();
            });
            this.harvestEmitters = null;
        }
    }

    public destroy(): void {
        super.destroy();
        this.cleanupManager.cleanUpAll();
    }
}
